import React, { useRef, useMemo } from "react"
import PropTypes from "prop-types"
import { useOutletContext, useParams } from "react-router-dom"

import { SIDEBAR_WIDTH } from "@components"
import { useAppContext } from "@components/AppContext"
import { indexOperation } from "@components/Project/stores/useProjectInvestmentsStore"
import { InvestmentShape } from "@components/Investment/shapes"
import Table, { CollapseTable } from "@components/Table"
import { InvestmentsTransferModal } from "@components/Investment"

import useColumnsConfig from "./helpers/useColumnsConfig"
import useHeaderActions from "./helpers/useHeaderActions"
import useExportColumns from "./helpers/useExportColumns"
import useSelectableActions from "./helpers/useSelectableActions"
import InvestmentsImportModal from "../InvestmentsImportModal"

const DEFAULT_SORT_COLUMN = "investor"


const InvestmentsTable = ({
  items = undefined,
  isOpenByDefault = false,
  hasTransferOption = false,
  table,
  title,
  openInvestmentDrawer,
  openCreateInvestmentModal,
}) => {
  const investmentsImportModal = useRef({})
  const investmentsTransferModal = useRef({})

  const { id: projectId } = useParams()

  const { isBroker } = useAppContext()
  const { indexInvestments: indexItems } = useOutletContext()

  const openInvestmentsImportModal = () =>
    investmentsImportModal.current.open()

  const size = "small"
  const style = { maxWidth: `calc(98vw - ${SIDEBAR_WIDTH}px)` }
  const scroll = { x: 800 }
  const bordered = true
  const className = "last-column-action"
  const columnsConfig = useColumnsConfig(isBroker, openInvestmentDrawer)
  const exportColumns = useExportColumns()

  const [ selectableActions, onSelectionChange ] = useSelectableActions({
    table,
    hasTransferOption,
    investmentsTransferModal,
  })

  const headerActions = useHeaderActions({
    selectableActions,
    openCreateInvestmentModal,
    openInvestmentsImportModal
  })

  const exportFileName = "investments"
  const hasHeaderFilters = false
  const defaultSortColumn = DEFAULT_SORT_COLUMN
  const operationParameters = useMemo(() => ({ projectId }), [projectId])

  const tableProps = {
    size,
    items,
    table,
    style,
    scroll,
    bordered,
    className,
    indexItems,
    columnsConfig,
    headerActions,
    exportColumns,
    exportFileName,
    indexOperation,
    hasHeaderFilters,
    defaultSortColumn,
    operationParameters,
  }

  if (selectableActions) {
    tableProps.isSelectable = true
    tableProps.onSelectionChange = onSelectionChange
  }

  return (
    <CollapseTable
      title={title}
      items={items}
      isOpenByDefault={isOpenByDefault}
    >
      <Table {...tableProps} />

      <InvestmentsImportModal
        modal={investmentsImportModal}
        indexInvestments={indexItems}
      />

      {
        hasTransferOption && (
          <InvestmentsTransferModal
            modal={investmentsTransferModal}
            onSuccess={() => table.current.indexItems(false)}
            projectId={projectId}
            investments={items}
          />
        )
      }
    </CollapseTable>
  )
}

InvestmentsTable.propTypes = {
  table: PropTypes.shape().isRequired,
  items: PropTypes.arrayOf(InvestmentShape),
  title: PropTypes.string.isRequired,
  isOpenByDefault: PropTypes.bool,
  hasTransferOption: PropTypes.bool,
  openInvestmentDrawer: PropTypes.func.isRequired,
  openCreateInvestmentModal: PropTypes.func.isRequired,
}

export default InvestmentsTable
